<template>
  <li
    class="tw-relative tw-top-0 tw-left-0 tw-mb-6 lg:tw-mb-12 tw-py-1 tw-rounded-lg tw-pl-12 lg:tw-pl-16 tw-pr-2 rule-item"
    :class="mainClass"
  >
    <div class="tw-grow tw-text-left tw-mr-4">
      <div
        v-if="showFreeBadge"
        class="tw-absolute tw-top-[-8px] tw-left-[-8px] lg:tw-left-[-35px]"
      >
        <div class="tw-relative tw-top-0 tw-left-0">
          <FreeBadgeSvg class="free-badge tw-w-[50px] tw-h-[50px] lg:tw-w-[70px] lg:tw-h-[70px]" />
          <div
            class="tw-absolute tw-top-[13px] lg:tw-top-[25px] tw-w-[50px] lg:tw-w-[70px] tw-text-center tw-text-mainbg1 tw-text-[10px] lg:tw-text-[14px] tw-font-bold"
          >
            FREE
          </div>
        </div>
      </div>
      <div class="rule-name tw-mb-2">
        {{ rule.spellingRuleName }}
      </div>
      <div v-if="hasScores" class="tw-mb-2">
        {{ rule.resultResume?.attemptCount }} Attempts. Best Score:
        {{ bestScore }}. Last Score: {{ lastScore }}
      </div>
    </div>
    <div class="tw-grid tw-grid-cols-2 tw-text-left lg:tw-flex lg:tw-justify-end">
      <div>
        <a href="" @click.prevent="runAction('play')" class="rule-link"> Play </a>
      </div>
      <div>
        <a href="" @click.prevent="runAction('wordList')" class="rule-link"> View Words </a>
      </div>
      <div>
        <a href="" @click.prevent="runAction('print')" class="rule-link"> Printables </a>
      </div>
      <div v-if="hasScores">
        <a href="" @click.prevent="runAction('results')" class="rule-link">
          View Score Details
        </a>
      </div>
    </div>
  </li>
</template>

<style scoped lang="postcss">
.locked-rule {
  @apply tw-bg-main1/25 tw-opacity-50;
}

.with-results .rule-name {
  @apply tw-text-lg tw-font-bold;
}

.first.rule-item {
  @apply tw-bg-color5/25;
}

.first .free-badge {
  @apply tw-fill-color5;
}

.middle.rule-item {
  @apply tw-bg-color2/25;
}

.middle .free-badge {
  @apply tw-fill-color2;
}

.last.rule-item {
  @apply tw-bg-primary/25;
}

.last .free-badge {
  @apply tw-fill-primary;
}

.rule-link {
  @apply lg:tw-inline-block lg:tw-px-3 tw-text-nowrap;
}
</style>

<script lang="ts">
import { SfWordCategory } from "@/types/spellingTypes";
import { PropType } from "vue";
import { defineComponent } from "vue";
import FreeBadgeSvg from "@/svg/FreeBadgeSvg.vue";
import formatPercent from "@/func/format/formatPercent";

export default defineComponent({
  components: { FreeBadgeSvg },
  emits: ["showWordList", "showPrintables", "showLocked", "showResults"],
  props: {
    parentGroup: {
      type: String as PropType<"first" | "middle" | "last" | "">,
      required: true,
    },
    rule: { type: Object as PropType<SfWordCategory>, required: true },
  },
  data() {
    return {};
  },
  computed: {
    hasScores(): boolean {
      return (
        this.rule.resultResume != undefined &&
        this.rule.resultResume.attemptCount > 0
      );
    },
    canOpen(): boolean {
      return this.$currentUser.value?.hasSpellingAccess || this.rule.is_free;
    },
    mainClass(): string {
      if (this.hasScores) {
        return this.parentGroup + " with-results";
      }
      return this.canOpen ? this.parentGroup : "locked-rule";
    },
    showFreeBadge(): boolean {
      const user = this.$currentUser.value?.hasSpellingAccess;
      return !user && this.rule.is_free;
    },
    bestScore(): string {
      return this.rule.resultResume
        ? formatPercent(this.rule.resultResume.bestScore, 1)
        : "";
    },
    lastScore(): string {
      return this.rule.resultResume
        ? formatPercent(this.rule.resultResume.lastScore, 1)
        : "";
    },
  },
  methods: {
    runAction(action: "play" | "wordList" | "print" | "results") {
      if (this.canOpen) {
        switch (action) {
          case "play":
            this.$router.push(this.rule.url);
            break;
          case "wordList":
            this.$emit("showWordList", this.rule);
            break;
          case "print":
            this.$emit("showPrintables", this.rule);
            break;
          case "results":
            this.$emit("showResults", this.rule);
            break;
        }
      } else {
        this.$emit("showLocked");
      }
    },
  },
});
</script>
